import React, { useEffect, useState, useRef } from 'react';
import { db } from '../FirebaseConfig';
import { collection, query, getDocs, limit, where } from 'firebase/firestore';
import Slider from 'react-slick';
import { motion,  AnimatePresence } from 'framer-motion';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";


const CustomSlider = ({ children, settings }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = React.Children.count(children);
  const sliderRef = useRef(null);

    const getResponsiveSettings = (width) => {
      let activeSettings = { ...settings };

      if (settings.responsive) {
        // Sort ascending (smallest → largest)
        const sortedBreakpoints = [...settings.responsive].sort((a, b) => a.breakpoint - b.breakpoint);

        for (const bp of sortedBreakpoints) {
          if (width <= bp.breakpoint) {
            activeSettings = { ...activeSettings, ...bp.settings };
          }
        }
      }

      return activeSettings;
    };

  
  const [activeSettings, setActiveSettings] = useState(getResponsiveSettings(window.innerWidth));

  useEffect(() => {
    const handleResize = () => {
      setActiveSettings(getResponsiveSettings(window.innerWidth));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (activeSettings.autoplay) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
      }, activeSettings.autoplaySpeed);
      return () => clearInterval(interval);
    }
  }, [activeSettings.autoplay, activeSettings.autoplaySpeed, totalSlides]);

  const showArrows = activeSettings.arrows && totalSlides > activeSettings.slidesToShow;
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };
  
  const transformValue = `translateX(-${(currentSlide / activeSettings.slidesToShow) * 100}%)`;

  return (
    <div className="relative overflow-hidden">
      <div
        className="flex transition-transform duration-500 ease-in-out"
        style={{ transform: transformValue }}
      >
        {React.Children.map(children, (child) => (
          <div className="flex-none px-2 py-4" style={{ width: `${100 / activeSettings.slidesToShow}%` }}>
            {child}
          </div>
        ))}
      </div>
      {showArrows && (
        <>
          <button
            onClick={prevSlide}
            className="absolute top-1/2 left-0 transform -translate-y-1/2 bg-white/20 text-white rounded-full p-2 ml-2 z-10"
          >
            &#10094;
          </button>
          <button
            onClick={nextSlide}
            className="absolute top-1/2 right-0 transform -translate-y-1/2 bg-white/20 text-white rounded-full p-2 mr-2 z-10"
          >
            &#10095;
          </button>
        </>
      )}
      {activeSettings.dots && (
        <div className="flex justify-center mt-4 space-x-2">
          {Array.from({ length: Math.ceil(totalSlides / activeSettings.slidesToScroll) }).map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === Math.floor(currentSlide / activeSettings.slidesToScroll) ? 'bg-emerald-400 w-4' : 'bg-gray-500'
              }`}
              onClick={() => setCurrentSlide(index * activeSettings.slidesToScroll)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProductGrid = ({ search, category }) => {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(true);

  const bestSellers = [
    { name: "Noise Cancelling Headphones", image: "https://images.pexels.com/photos/10567238/pexels-photo-10567238.jpeg?auto=compress&cs=tinysrgb&w=600" },
    { name: "Power Bank 20,000mAh", image: "https://images.pexels.com/photos/16814788/pexels-photo-16814788/free-photo-of-man-charging-cellphone-with-powerbank.jpeg?auto=compress&cs=tinysrgb&w=600" },
    { name: "Laptop Bag", image: "https://images.pexels.com/photos/936081/pexels-photo-936081.jpeg?auto=compress&cs=tinysrgb&w=600" },
    { name: "Bluetooth Speaker", image: "https://images.pexels.com/photos/128611/pexels-photo-128611.jpeg?auto=compress&cs=tinysrgb&w=600" },
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      const q = query(
        collection(db, 'Products'),
        where('approved', '==', true), // ✅ Only approved products
        limit(5) // ✅ Only 5 products
      );

      const snapshot = await getDocs(q);
      const fetched = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(fetched); // ✅ No filter needed after this
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    const hidePopup = localStorage.getItem('hideiBuyUNNPopup');
    if (!hidePopup) {
      setShowModal(true);
    }
  }, []);

  const handleMaybeLater = () => {
    localStorage.setItem('hideiBuyUNNPopup', 'true');
    setShowModal(false);
  };

  const filtered = products.filter((item) => {
    const name = item?.name || "";
    const categoryValue = item?.category || "";

    const matchesSearch = name.toLowerCase().includes(search?.toLowerCase() || "");
    const matchesCategory = category ? categoryValue === category : true;

    return matchesSearch && matchesCategory;
  });

  const productsToShowInSlider = filtered;

  const bestSellersSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
  };

  const productSliderSettings = {
    dots: true,
    infinite: productsToShowInSlider.length > 3,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: false,
    arrows: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: productsToShowInSlider.length > 2,
          dots: true
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: productsToShowInSlider.length > 1,
          initialSlide: 0,
          dots: true
        }
      }
    ]
  };

   return (
    <div className="relative min-h-screen bg-[#0A100D] font-sans">
      
      {/* Glassy Pop-up Modal */}
      {/* <AnimatePresence>
        {showModal && (
          <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ duration: 0.3 }}
              className="fixed bottom-4 right-4 z-50 w-[90%] max-w-sm"
            >
              <div className="bg-emerald-950/60 backdrop-blur-xl border border-emerald-900 rounded-3xl p-6 shadow-2xl text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-emerald-400">Join The uCart Group</h2>
                <p className="mt-2 text-sm sm:text-base text-gray-300">
                  Connect with the community and stay updated with new listings!
                </p>
                <motion.button
                  onClick={() => window.location.href = "https://chat.whatsapp.com/LmcchD8QjgBGiwHe1sNgk7?mode=ac_t"}
                  className="mt-4 w-full py-3 bg-emerald-600 text-white rounded-full font-bold hover:bg-emerald-500 transition-colors shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Join Now
                </motion.button>
                <button
                  onClick={handleMaybeLater}
                  className="mt-3 block mx-auto text-xs text-gray-400 hover:text-white transition-colors underline"
                >
                  Maybe Later
                </button>
              </div>
            </motion.div>

        )}
      </AnimatePresence> */}

      <div className="p-6">
        {/* Best Sellers Slider Section */}
        <h2 className="text-2xl font-bold text-emerald-300 text-center mt-8 mb-6">Best Sellers</h2>
        <div className="my-10 mx-auto max-w-4xl lg:max-w-6xl xl:max-w-7xl">
          <CustomSlider settings={bestSellersSliderSettings}>
            {bestSellers.map((product, index) => (
              <div key={index} className="px-2">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-52 sm:h-64 object-cover rounded-3xl shadow-lg border border-emerald-800"
                />
                <h2 className="text-white mt-4 text-center text-lg sm:text-xl font-semibold">
                  {product.name}
                </h2>
              </div>
            ))}
          </CustomSlider>
        </div>

        {/* Recent Products Slider */}
        <h2 className="text-2xl font-bold text-emerald-300 text-center mt-8 mb-6">Recent Products</h2>
        <div className="my-10 mx-auto max-w-4xl lg:max-w-6xl xl:max-w-7xl">
          {productsToShowInSlider.length > 0 ? (
            <CustomSlider settings={productSliderSettings}>
              {productsToShowInSlider.map((product) => (
                <div key={product.id} className="px-2 py-4">
                  <div className="bg-emerald-950/50 backdrop-blur-lg text-white rounded-3xl p-4 shadow-xl hover:scale-[1.02] transition-transform h-full flex flex-col justify-between border border-emerald-800">
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="w-full h-44 sm:h-52 object-cover rounded-xl "
                    />
                    <h3 className="text-lg font-semibold mt-3 text-emerald-300 line-clamp-2">{product.name}</h3>
                    <p className="text-white font-bold text-sm sm:text-base mt-2">₦{product.price}</p>
                  </div>
                </div>
              ))}
            </CustomSlider>
          ) : (
            <p className="col-span-full text-center text-gray-400 text-sm">No products found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductGrid;
